import { ArrowLeft, ExternalLink, Globe } from 'lucide-react';
import type { LoaderFunctionArgs, MetaFunction } from 'react-router';
import { Link, useLoaderData } from 'react-router';
import { Badge } from '~/components/ui/badge';
import { Button } from '~/components/ui/button';
import { getSpecBySlug } from '~/lib/db/queries/specs';
import {
  getImplementation,
  inferDepth,
  itemCount,
  missingCoverage,
  orderedFeatures,
} from '~/lib/implementation';
import type {
  ImplementationDepth,
  ImplField,
  ImplRoute,
  ImplTable,
} from '~/lib/implementation/types';

export const meta: MetaFunction<typeof loader> = ({ loaderData }) => [
  {
    title: loaderData
      ? `${loaderData.impl.meta.name} — ${loaderData.spec.name} — Specly`
      : 'Implementation — Specly',
  },
];

export async function loader({ params }: LoaderFunctionArgs) {
  const spec = await getSpecBySlug(params.slug!);
  if (!spec) throw new Response('Not Found', { status: 404 });

  const impl = getImplementation(spec.slug, params.implSlug!);
  if (!impl) throw new Response('Not Found', { status: 404 });

  const specFeatureSlugs = spec.specFeatures.map((sf) => sf.feature.slug);
  const featureNames = Object.fromEntries(
    spec.specFeatures.map((sf) => [sf.feature.slug, sf.feature.name]),
  );

  return {
    spec,
    impl,
    featureNames,
    missing: missingCoverage(impl, specFeatureSlugs),
    counts: itemCount(impl),
    actualDepth: inferDepth(impl),
    features: orderedFeatures(impl),
  };
}

const DEPTH_COLOR: Record<ImplementationDepth, string> = {
  overview: 'bg-sky-100 text-sky-800 border-sky-200',
  structural: 'bg-violet-100 text-violet-800 border-violet-200',
  detailed: 'bg-emerald-100 text-emerald-800 border-emerald-200',
};

const METHOD_COLOR: Record<string, string> = {
  GET: 'bg-blue-100 text-blue-700',
  POST: 'bg-green-100 text-green-700',
  PUT: 'bg-amber-100 text-amber-700',
  PATCH: 'bg-amber-100 text-amber-700',
  DELETE: 'bg-red-100 text-red-700',
  ANY: 'bg-gray-100 text-gray-700',
};

function fieldSummary(f: ImplField): string {
  const parts: string[] = [];
  if (f.type) parts.push(f.type);
  if (f.primaryKey) parts.push('PK');
  if (f.unique) parts.push('unique');
  if (f.nullable) parts.push('nullable');
  if (f.references)
    parts.push(
      `→ ${f.references.table}${f.references.column ? '.' + f.references.column : ''}`,
    );
  if (f.default !== undefined) parts.push(`default ${f.default}`);
  return parts.join(' · ');
}

function TableBlock({ table }: { table: ImplTable }) {
  return (
    <div className="rounded-lg border border-border">
      <div className="flex items-center justify-between px-3 py-2 bg-muted/40 border-b border-border">
        <code className="text-xs font-mono font-semibold">{table.name}</code>
        {table.fields && (
          <span className="text-[10px] text-muted-foreground">
            {table.fields.length} fields
          </span>
        )}
      </div>
      {table.description && (
        <p className="px-3 py-2 text-xs text-muted-foreground border-b border-border">
          {table.description}
        </p>
      )}
      {table.fields && table.fields.length > 0 && (
        <ul className="divide-y divide-border text-xs">
          {table.fields.map((f) => (
            <li key={f.name} className="px-3 py-1.5 flex items-start gap-3">
              <code className="font-mono text-foreground min-w-0 flex-shrink-0">
                {f.name}
              </code>
              <span className="text-muted-foreground min-w-0 flex-1">
                {fieldSummary(f) || '—'}
                {f.description && (
                  <span className="text-muted-foreground/80">
                    {' '}
                    — {f.description}
                  </span>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
      {table.indexes && table.indexes.length > 0 && (
        <div className="px-3 py-2 text-[11px] text-muted-foreground border-t border-border">
          <span className="font-medium">Indexes:</span>{' '}
          {table.indexes
            .map(
              (idx) =>
                `${idx.unique ? 'UNIQUE ' : ''}(${idx.columns.join(', ')})`,
            )
            .join(' · ')}
        </div>
      )}
      {table.notes && (
        <p className="px-3 py-2 text-xs text-amber-700 bg-amber-50 border-t border-border">
          {table.notes}
        </p>
      )}
    </div>
  );
}

function RouteRow({ r }: { r: ImplRoute }) {
  return (
    <div className="py-1.5">
      <div className="flex items-center gap-2 flex-wrap">
        <span
          className={`text-[11px] font-mono font-semibold px-1.5 py-0.5 rounded ${METHOD_COLOR[r.method ?? 'ANY']}`}
        >
          {r.method ?? 'ANY'}
        </span>
        <code className="text-xs font-mono">{r.path}</code>
        {r.authRequired && (
          <Badge variant="outline" className="text-[10px]">
            auth
          </Badge>
        )}
      </div>
      {r.description && (
        <p className="text-xs text-muted-foreground mt-0.5 ml-2">
          {r.description}
        </p>
      )}
      {r.handler && (
        <p className="text-[11px] text-muted-foreground/80 mt-0.5 ml-2 font-mono">
          {r.handler}
        </p>
      )}
      {r.params && r.params.length > 0 && (
        <p className="text-[11px] text-muted-foreground mt-0.5 ml-2">
          Params:{' '}
          {r.params
            .map(
              (p) =>
                `${p.name}${p.required ? '' : '?'}:${p.type ?? 'any'} (${p.in})`,
            )
            .join(', ')}
        </p>
      )}
      {r.responses && r.responses.length > 0 && (
        <p className="text-[11px] text-muted-foreground mt-0.5 ml-2 font-mono">
          {r.responses
            .map((res) => `${res.status} ${res.shape ?? ''}`)
            .join(' · ')}
        </p>
      )}
    </div>
  );
}

export default function ImplementationDetail() {
  const { spec, impl, featureNames, missing, counts, actualDepth, features } =
    useLoaderData<typeof loader>();

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6">
        <Link
          to={`/specs/${spec.slug}/implementations`}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4"
        >
          <ArrowLeft className="size-4" />
          All implementations
        </Link>

        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <h1 className="text-3xl font-bold">{impl.meta.name}</h1>
              <span
                className={`text-xs px-2 py-0.5 rounded-full border font-medium ${DEPTH_COLOR[actualDepth]}`}
              >
                {actualDepth}
              </span>
            </div>
            <p className="text-muted-foreground max-w-3xl mt-1">
              {impl.meta.description}
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              For spec:{' '}
              <Link
                to={`/specs/${spec.slug}`}
                className="text-foreground hover:underline font-medium"
              >
                {spec.name}
              </Link>
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {impl.meta.demoUrl && (
              <Button
                variant="outline"
                size="sm"
                render={
                  <a
                    href={impl.meta.demoUrl}
                    target="_blank"
                    rel="noreferrer"
                  />
                }
              >
                <Globe data-icon="inline-start" />
                Demo
              </Button>
            )}
            {impl.meta.sourceUrl && (
              <Button
                variant="outline"
                size="sm"
                render={
                  <a
                    href={impl.meta.sourceUrl}
                    target="_blank"
                    rel="noreferrer"
                  />
                }
              >
                <ExternalLink data-icon="inline-start" />
                Source
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Stack strip */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        {(
          [
            ['Frontend', impl.meta.stack.frontend],
            ['Backend', impl.meta.stack.backend],
            ['Database', impl.meta.stack.database],
            ['ORM', impl.meta.stack.orm],
            ['Auth', impl.meta.stack.auth],
            ['Hosting', impl.meta.stack.hosting],
          ] as [string, string | undefined][]
        )
          .filter(([, v]) => v)
          .map(([label, value]) => (
            <div
              key={label}
              className="rounded-lg border border-border bg-card px-3 py-2"
            >
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                {label}
              </div>
              <div className="text-xs font-medium mt-0.5">{value}</div>
            </div>
          ))}
        {impl.meta.stack.other &&
          Object.entries(impl.meta.stack.other).map(([k, v]) => (
            <div
              key={k}
              className="rounded-lg border border-border bg-card px-3 py-2"
            >
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                {k}
              </div>
              <div className="text-xs font-medium mt-0.5">{v}</div>
            </div>
          ))}
      </div>

      {/* Counts */}
      <div className="flex flex-wrap gap-2 text-xs text-muted-foreground mb-6">
        <Badge variant="outline">{counts.tables} tables</Badge>
        <Badge variant="outline">{counts.routes} routes</Badge>
        <Badge variant="outline">{counts.modules} modules</Badge>
        <Badge variant="outline">{counts.packages} packages</Badge>
        <Badge variant="outline">{counts.envVars} env vars</Badge>
        {missing.length > 0 && (
          <Badge variant="destructive">
            {missing.length} feature{missing.length === 1 ? '' : 's'} uncovered
          </Badge>
        )}
      </div>

      {/* Setup */}
      {impl.setup && (
        <section className="mb-8">
          <h2 className="text-lg font-semibold mb-2">Setup</h2>
          <pre className="rounded-lg border border-border bg-muted/40 p-4 text-xs whitespace-pre-wrap font-mono">
            {impl.setup}
          </pre>
        </section>
      )}

      {/* Spec-wide packages */}
      {impl.packages && impl.packages.length > 0 && (
        <section className="mb-8">
          <h2 className="text-lg font-semibold mb-2">Packages</h2>
          <div className="rounded-lg border border-border divide-y divide-border text-sm">
            {impl.packages.map((p) => (
              <div
                key={p.name}
                className="px-3 py-2 flex items-center gap-3 flex-wrap"
              >
                <code className="text-xs font-mono font-medium">{p.name}</code>
                {p.version && (
                  <span className="text-xs text-muted-foreground">
                    {p.version}
                  </span>
                )}
                {p.ecosystem && (
                  <Badge variant="outline" className="text-[10px]">
                    {p.ecosystem}
                  </Badge>
                )}
                {p.purpose && (
                  <span className="text-xs text-muted-foreground">
                    — {p.purpose}
                  </span>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Spec-wide env vars */}
      {impl.envVars && impl.envVars.length > 0 && (
        <section className="mb-8">
          <h2 className="text-lg font-semibold mb-2">Environment</h2>
          <div className="rounded-lg border border-border divide-y divide-border text-sm">
            {impl.envVars.map((e) => (
              <div key={e.name} className="px-3 py-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <code className="text-xs font-mono font-medium">
                    {e.name}
                  </code>
                  {e.required && (
                    <Badge variant="outline" className="text-[10px]">
                      required
                    </Badge>
                  )}
                  {e.example && (
                    <code className="text-[11px] text-muted-foreground font-mono">
                      = {e.example}
                    </code>
                  )}
                </div>
                {e.description && (
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {e.description}
                  </p>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Per-feature implementation */}
      <section>
        <h2 className="text-lg font-semibold mb-3">Feature implementations</h2>
        <div className="flex flex-col gap-5">
          {features.map((f) => (
            <div
              key={f.featureSlug}
              className="rounded-xl border border-border bg-card p-5"
            >
              <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
                <div>
                  <h3 className="text-base font-semibold">
                    {featureNames[f.featureSlug] ?? f.featureSlug}
                  </h3>
                  <code className="text-[11px] text-muted-foreground font-mono">
                    {f.featureSlug}
                  </code>
                </div>
              </div>
              {f.summary && (
                <p className="text-sm text-muted-foreground mb-3">
                  {f.summary}
                </p>
              )}

              {f.tables && f.tables.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                    Tables
                  </h4>
                  <div className="flex flex-col gap-2">
                    {f.tables.map((t) => (
                      <TableBlock key={t.name} table={t} />
                    ))}
                  </div>
                </div>
              )}

              {f.routes && f.routes.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                    Routes
                  </h4>
                  <div className="rounded-lg border border-border divide-y divide-border px-3">
                    {f.routes.map((r, i) => (
                      <RouteRow
                        key={`${r.method ?? 'ANY'}-${r.path}-${i}`}
                        r={r}
                      />
                    ))}
                  </div>
                </div>
              )}

              {f.modules && f.modules.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                    Modules
                  </h4>
                  <ul className="text-xs flex flex-col gap-1">
                    {f.modules.map((m) => (
                      <li key={m.path}>
                        <code className="font-mono font-medium">{m.path}</code>
                        <span className="text-muted-foreground">
                          {' '}
                          — {m.purpose}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {f.packages && f.packages.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                    Packages
                  </h4>
                  <div className="flex flex-wrap gap-1.5">
                    {f.packages.map((p) => (
                      <Badge
                        key={p.name}
                        variant="outline"
                        className="text-[11px]"
                      >
                        {p.name}
                        {p.version ? ` ${p.version}` : ''}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {f.envVars && f.envVars.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                    Env vars
                  </h4>
                  <div className="flex flex-wrap gap-1.5">
                    {f.envVars.map((e) => (
                      <Badge
                        key={e.name}
                        variant="outline"
                        className="text-[11px]"
                      >
                        {e.name}
                        {e.required ? ' *' : ''}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {f.notes && (
                <p className="text-xs text-amber-700 bg-amber-50 rounded px-3 py-2 mt-2">
                  {f.notes}
                </p>
              )}
            </div>
          ))}
        </div>

        {missing.length > 0 && (
          <div className="mt-6 rounded-xl border border-dashed border-amber-300 bg-amber-50 p-4">
            <p className="text-sm font-semibold text-amber-900 mb-1">
              Uncovered features
            </p>
            <p className="text-xs text-amber-800 mb-2">
              The spec includes features this implementation doesn't describe.
              Contributions welcome.
            </p>
            <div className="flex flex-wrap gap-1.5">
              {missing.map((slug) => (
                <Badge key={slug} variant="outline" className="text-[11px]">
                  {featureNames[slug] ?? slug}
                </Badge>
              ))}
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
